import { apiSlice } from "@/redux/apiSlice";
import { EventInput, Event } from "@/types/event";
import { EventStatus } from "@/types/enums";
import eventData from "@/mock/event.json";

// Import mock data from JSON file
let mockEvents = [...eventData];

// Helper function to calculate event status based on dates
const calculateEventStatus = (startDate: string, endDate: string): EventStatus => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);
  
  if (end < today) {
    return EventStatus.Completed;
  } else if (start <= today && today <= end) {
    return EventStatus.Ongoing;
  } else {
    return EventStatus.Upcoming;
  }
};

export const eventApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEvents: builder.query({
      providesTags: ["Events"],
      queryFn: async (params: EventInput) => {
        const { query: queryString, org_id } = params;
        
        // Parse query string to extract parameters
        const urlParams = new URLSearchParams(queryString);
        const search_key = urlParams.get('search_key');
        const sort_key = urlParams.get('sort_key') || 'id';
        const sort_order = urlParams.get('sort_order') || 'desc';
        const limit = parseInt(urlParams.get('limit') || '10');
        const offset = parseInt(urlParams.get('offset') || '0');
        const type = urlParams.get('type');
        
        let filteredEvents = mockEvents.filter(event => event.org_id === org_id);
        
        // Apply search filter
        if (search_key && search_key.trim() !== "") {
          filteredEvents = filteredEvents.filter(event => 
            event.name.toLowerCase().includes(search_key.toLowerCase())
          );
        }
        
        // Apply sorting
        filteredEvents.sort((a, b) => {
          let aValue = a[sort_key as keyof typeof a];
          let bValue = b[sort_key as keyof typeof b];
          
          // Handle different data types
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          }
          
          if (sort_order === 'asc') {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          } else {
            return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
          }
        });
        
        // Always calculate status based on current dates for accuracy
        const eventsWithStatus = filteredEvents.map(event => ({
          ...event,
          status: calculateEventStatus(event.start_date, event.end_date)
        }));
        
        // Apply pagination
        const totalCount = eventsWithStatus.length;
        const paginatedEvents = eventsWithStatus.slice(offset, offset + limit);
        
        // Return mock response
        return {
          data: {
            data: paginatedEvents,
            filtered_counts: totalCount,
            total_counts: mockEvents.length
          }
        };
      },
    }),
    createEvent: builder.mutation({
      invalidatesTags: ["Events"],
      queryFn: async (data: Event) => {
        const newEvent = {
          id: Date.now(),
          name: data.name,
          is_shareable: data.is_shareable,
          booking_type: data.booking_type,
          facility_name: data.facility_name || "",
          color: data.color,
          max_attendees: data.max_attendees,
          location: data.location || "",
          description: data.description || "",
          start_date: data.start_date,
          end_date: data.end_date,
          org_id: data.org_id || 1,
          created_at: new Date().toISOString(),
          created_by: 1,
          is_deleted: false,
          status: data.status || calculateEventStatus(data.start_date, data.end_date),
        };
        
        mockEvents.push(newEvent);
        return { data: newEvent };
      },
    }),
    updateEvent: builder.mutation({
      invalidatesTags: ["Events"],
      query: (data: Event) => {
        // Update mock data
        const index = mockEvents.findIndex(event => event.id === data.id);
        if (index !== -1) {
          const updatedEvent = { 
            ...mockEvents[index], 
            ...data,
            // Recalculate status if dates changed
            status: data.status || calculateEventStatus(data.start_date, data.end_date)
          };
          mockEvents[index] = updatedEvent;
        }
        
        return {
          url: `/events/${data.id}`,
          method: "PUT",
          body: data,
          response: data
        };
      },
    }),
    deleteEvent: builder.mutation({
      invalidatesTags: ["Events"],
      queryFn: async ({ event_id, org_id }: { event_id: number; org_id: number }) => {
        // Remove from mock data
        const index = mockEvents.findIndex(event => event.id === event_id);
        if (index !== -1) {
          mockEvents.splice(index, 1);
        }
        
        // Return mock success response
        return { data: { success: true, message: "Event deleted successfully" } };
      },
    }),
  }),
});

export const {
  useGetEventsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
} = eventApi;
